import { renderElement } from '../dom';
import { buildEventEmitter, EventEmitter } from '../events';
import { commafyInteger } from '../text';
import { getTime, onNextFrame } from '../timers';

type AnimateNumberValue = {
  from: number;
  to: number;
};

const isAnimateNumberValue = (value: any): value is AnimateNumberValue =>
  typeof value === 'object' &&
  typeof value.from === 'number' &&
  typeof value.to === 'number';

type AnimateNumberOptions = {
  commafy?: boolean;
  duration?: number;
  prefix?: string;
  suffix?: string;
};

const isAnimateNumberOptions = (value: any): value is AnimateNumberOptions =>
  typeof value === 'undefined' ||
  (typeof value === 'object' &&
    (typeof value.commafy === 'undefined' ||
      typeof value.commafy === 'boolean') &&
    (typeof value.duration === 'undefined' ||
      typeof value.duration === 'number') &&
    (typeof value.prefix === 'undefined' || typeof value.prefix === 'string') &&
    (typeof value.suffix === 'undefined' || typeof value.suffix === 'string'));

const buildNumberAnimation = (
  { from, to }: AnimateNumberValue,
  {
    commafy = true,
    duration = 1000,
    prefix = '',
    suffix = '',
  }: AnimateNumberOptions,
) => {
  const emitter = buildEventEmitter<string>(['end', 'start', 'update']);

  let started = false;
  const startTime = getTime();

  const formatValue = (value: number) => {
    const displayNumber = commafy ? commafyInteger(value) : String(value);

    return `${prefix}${displayNumber}${suffix}`;
  };

  const step = () => {
    const currentTime = getTime();
    const timeElapsed = currentTime - startTime;

    if (!started) {
      started = true;
      emitter.emit('start', formatValue(from));
    }

    const amount = from + Math.floor((timeElapsed / duration) * (to - from));
    emitter.emit('update', formatValue(amount));

    if (timeElapsed >= duration) {
      emitter.emit('end', formatValue(to));
    } else {
      onNextFrame(step);
    }
  };

  onNextFrame(step);

  return emitter;
};

export const animateNumber = (value: any, options: any) => {
  if (!isAnimateNumberValue(value)) {
    throw new Error(
      'FLMPiano.animateNumber expected the first parameter to be `{ from: number, to: number }`',
    );
  }

  if (!isAnimateNumberOptions(options)) {
    throw new Error(
      'FLMPiano.animateNumber expected the second parameter to be omitted or `{ commafy?: boolean, duration?: number, prefix?: string, suffix?: string }`',
    );
  }

  return buildNumberAnimation(value, options);
};

export const animateNumberElement = (
  selector: string,
  value: any,
  options: any,
) => {
  if (typeof selector !== 'string') {
    throw new Error(
      'FLMPiano.animateElementNumber expected the first parameter to be a string selector',
    );
  }
  const element = document.querySelector(selector);
  if (!element) {
    throw new Error(
      `FLMPiano.animateElementNumber could not find an element matching the selector '${selector}'`,
    );
  }

  const animation = animateNumber(value, options);

  applyNumberAnimationToElement(element, animation);

  return animation;
};

const applyNumberAnimationToElement = (
  element: Element,
  animation: EventEmitter<string>,
) => {
  animation.addEventListener('*', (value) => {
    element.innerHTML = value;
  });
};

export const renderAnimatedNumber = (value: any, options: any) => {
  const element = renderElement('div');
  const animation = animateNumber(value, options);

  applyNumberAnimationToElement(element, animation);

  return {
    animation,
    element,
  };
};
